import axios from '@/lib/axios'
import { useQuery } from '@tanstack/vue-query'
import { type MaybeRef, get } from '@vueuse/core'
import { computed } from 'vue'

import { CustomAxiosRequestConfig } from '@/interfaces/customAxiosRequestConfig'

import type { TripReason } from '@/modules/companies/interfaces/tripReason'

export interface TripReasonOptionsResponse {
	required: boolean
	allowOther: boolean
	/** Motivo del departamento del viajero, si hoy se le puede ofrecer. */
	defaultId: number | null
	options: TripReason[]
}

/**
 * Motivos de viaje que se le ofrecen a quien reserva para `travelerId`: los del
 * catálogo de la empresa del viajero, activos y vigentes hoy, más las dos reglas de
 * la empresa —si el motivo es obligatorio (sin «Sin motivo de viaje») y si se
 * permite escribir «Otro»—.
 *
 * Si el endpoint falla (o todavía no hay viajero), `isFallback` queda en true y el consumidor vuelve a las
 * opciones fijas de siempre: BookingV2 sigue aceptando el motivo como texto, así que
 * reservar nunca se bloquea por esta lista.
 */
export const getTripReasonOptions = async (
	travelerId: number
): Promise<TripReasonOptionsResponse> => {
	const { data } = await axios.get<TripReasonOptionsResponse>(
		`/api/booking/travelers/${travelerId}/trip-reasons`,
		{ errorHandling: false } as CustomAxiosRequestConfig
	)

	return data
}

/**
 * Con varios viajeros el selector muestra el catálogo del primero, pero BookingV2
 * valida cada solicitud con el del suyo: un motivo limitado a un departamento haría
 * que unas se crearan y otras no. Regresa el primer viajero (después del primero)
 * al que no se le ofrece el motivo elegido, o null.
 */
export const travelerWithoutTripReason = async <T extends { id: number }>(
	tripReasonId: number | null,
	travelers: T[]
): Promise<T | null> => {
	if (tripReasonId === null) return null

	// En paralelo: con muchos viajeros, en serie se alargaba la espera del botón.
	const offered = await Promise.all(
		travelers.slice(1).map(async (traveler) => {
			try {
				const { options } = await getTripReasonOptions(traveler.id)
				return options.some((option) => option.id === tripReasonId)
			} catch {
				// Sin catálogo no se puede saber; BookingV2 valida al crear.
				return true
			}
		})
	)
	const index = offered.indexOf(false)

	if (index !== -1) return travelers[index + 1]

	return null
}

const useTripReasonOptions = (travelerId: MaybeRef<number | undefined>) => {
	const id = computed(() => get(travelerId))

	// `isInitialLoading` y no `isLoading`: en vue-query v4 una query deshabilitada
	// (sin viajero aún) se queda en `loading` para siempre.
	const enabled = computed(() => !!id.value && !Number.isNaN(id.value))

	const { data, isInitialLoading, isError } = useQuery(
		['tripReasonOptions', id],
		() => getTripReasonOptions(id.value!),
		{
			enabled,
			retry: false,
			staleTime: 60 * 1000,
		}
	)

	return {
		options: computed(() => data.value?.options ?? []),
		required: computed(() => data.value?.required ?? false),
		allowOther: computed(() => data.value?.allowOther ?? true),
		defaultId: computed(() => data.value?.defaultId ?? null),
		isLoading: isInitialLoading,
		// Sin viajero no hay a qué empresa preguntarle: también opciones fijas. Un
		// refresco que falla con datos ya cargados no cuenta: se quedan esos datos y
		// la selección no se limpia.
		isFallback: computed(
			() => (isError.value && !data.value) || !enabled.value
		),
	}
}

export default useTripReasonOptions
